import type { VodChapter } from 'src/generated/graphql-manager';

import { useLayoutEffect, useState } from 'react';

type Animation = 'moveUp' | 'moveDown';

/**
 * Custom hook to manage chapter animations
 *
 * @param chapters - Current list of chapters
 * @param previousChapters - Previous list of chapters (from usePrevious hook)
 * @returns Animation states and handlers
 */
export function useChapterAnimations(chapters: VodChapter[], previousChapters: VodChapter[] | undefined) {
  const [animations, setAnimations] = useState<{ id: VodChapter['id']; type: Animation }[]>([]);

  // Clean up animations after they finish
  const onAnimationEnd = (event: React.AnimationEvent<HTMLDivElement>, id: VodChapter['id']) => {
    const animationName = event.animationName.split('_')[1] as Animation;
    setAnimations((state) => state.filter((animation) => animation.id !== id || animation.type !== animationName));
  };

  // Detect order changes and trigger animations
  useLayoutEffect(() => {
    // Skip if chapters or previousChapters aren't available yet, or if count changed (chapters added/removed instead of moved)
    if (!previousChapters || chapters.length !== previousChapters.length) return;

    chapters.forEach((chapter, index) => {
      const previousIndex = previousChapters.findIndex(({ id }) => id === chapter.id);

      if (previousIndex === -1 || previousIndex === index) return;

      setAnimations((state) => [
        ...state,
        {
          id: chapter.id,
          type: previousIndex > index ? 'moveUp' : 'moveDown',
        },
      ]);
    });
  }, [chapters, previousChapters]);

  return { animations, onAnimationEnd };
}
